import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Handshake, Sparkles, Loader2, Copy, Check, Users, TrendingUp,
  Clock, ShieldCheck, ArrowLeft, Gift, Crown, Bell, Megaphone, Zap, AtSign,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Perks of becoming a Lumera collaborator.
const PERKS = [
  { icon: Crown, title: 'Premium + Slidecon, free', desc: 'Full access to every Lumera & Slidecon feature while you’re active.' },
  { icon: Bell, title: 'First to know', desc: 'Early access & previews of new features before anyone else.' },
  { icon: Gift, title: 'Gifts from the team', desc: 'Surprise thank-you gifts and Lumera merch sent your way.' },
  { icon: Megaphone, title: 'Get featured', desc: 'Shout-outs & mentions across Lumera’s social accounts.' },
  { icon: AtSign, title: 'Direct line to us', desc: 'A private channel to the Lumera team for ideas & support.' },
  { icon: Zap, title: '15-day trials for your audience', desc: 'Everyone who uses your code unlocks a 15-day Lumera + Slidecon trial.' },
];

const PerksGrid = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="collab-perks">
    {PERKS.map((p) => (
      <div key={p.title} className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/12 text-primary">
            <p.icon className="h-4 w-4" />
          </span>
          <span className="font-display font-semibold text-foreground">{p.title}</span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{p.desc}</p>
      </div>
    ))}
  </div>
);

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
      <Icon className="h-4 w-4 text-primary" /> {label}
    </div>
    <div className="mt-2 font-display text-3xl font-bold text-foreground" data-testid={`collab-stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>{value}</div>
  </div>
);

const CollabPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ requested_code: '', platform: '', handle: '', audience_size: '', message: '' });

  const [apps, setApps] = useState(null);
  const isAdmin = user && ['admin', 'super_admin'].includes(user.role);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/collab/me`, { withCredentials: true });
      setData(res.data);
    } catch (e) { /* not logged in */ } finally { setLoading(false); }
  }, [user]);

  const loadApps = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/collab/admin/applications?status=pending`, { withCredentials: true });
      setApps(res.data.applications || []);
    } catch (e) { /* noop */ }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadApps(); }, [loadApps]);

  const submitApplication = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/collab/apply`, form, { withCredentials: true });
      toast.success('Application submitted! We’ll review it shortly.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit application.');
    } finally { setSubmitting(false); }
  };

  const review = async (id, action) => {
    try {
      await axios.post(`${BACKEND_URL}/api/collab/admin/applications/${id}/${action}`, {}, { withCredentials: true });
      toast.success(`Application ${action}d.`);
      await loadApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${action}.`);
    }
  };

  const shareLink = data?.code ? `${window.location.origin}/login?ref=${data.code}` : '';
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success('Referral link copied!'); }
    catch (e) { toast.error('Copy failed'); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6 text-center">
        <Handshake className="h-10 w-10 text-primary mb-4" />
        <h1 className="font-display text-3xl font-bold text-foreground">Become a Lumera Collaborator</h1>
        <p className="mt-2 text-muted-foreground max-w-md">Sign in or create a free account first, then apply to join the program.</p>
        <Button className="mt-6" data-testid="collab-signin-btn" onClick={() => navigate('/login')}>Sign in to apply</Button>
      </div>
    );
  }

  const status = data?.status;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-4xl px-5 py-10">
        <button onClick={() => navigate('/dashboard')} className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="collab-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to app
        </button>

        {/* APPROVED — Influencer dashboard */}
        {status === 'approved' && (
          <div data-testid="collab-dashboard">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <Badge className="bg-primary/15 text-primary border-primary/30">Active Collaborator</Badge>
            </div>
            <h1 className="font-display text-4xl font-bold text-foreground">Your Collaborator Dashboard</h1>
            <p className="mt-1 text-muted-foreground">Premium + Slidecon are unlocked on your account. Share your code to give friends a 15-day trial.</p>

            {(() => {
              const last7 = (data.stats?.series || []).slice(-7).reduce((a, s) => a + s.count, 0);
              return last7 > 0 ? (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3" data-testid="collab-week-nudge">
                  <Sparkles className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm text-foreground"><strong>{last7}</strong> {last7 === 1 ? 'person' : 'people'} joined via your code this week — keep sharing! 🎉</span>
                </div>
              ) : null;
            })()}

            <div className="mt-6 rounded-xl border border-border bg-card p-5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your referral link</Label>
              <div className="mt-2 flex flex-col sm:flex-row gap-2">
                <code className="flex-1 rounded-lg bg-muted px-4 py-3 text-sm text-foreground break-all" data-testid="collab-share-link">{shareLink}</code>
                <Button onClick={copyLink} className="shrink-0" data-testid="collab-copy-btn">
                  {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />} {copied ? 'Copied' : 'Copy link'}
                </Button>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">Code: <span className="font-semibold text-primary">{data.code}</span></p>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Stat icon={Users} label="Total Sign-ups" value={data.stats?.total_signups ?? 0} />
              <Stat icon={Clock} label="Last 30 Days" value={data.stats?.last_30_days ?? 0} />
            </div>

            {/* 30-day bar chart */}
            <div className="mt-6 rounded-xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-4">
                <TrendingUp className="h-4 w-4 text-primary" /> Sign-ups · last 30 days
              </div>
              <ChartBars series={data.stats?.series || []} />
            </div>

            <h2 className="mt-8 mb-3 font-display text-2xl font-bold text-foreground">Your collaborator perks</h2>
            <PerksGrid />
          </div>
        )}

        {/* PENDING */}
        {status === 'pending' && (
          <div className="rounded-xl border border-border bg-card p-8 text-center" data-testid="collab-pending">
            <Clock className="h-9 w-9 text-primary mx-auto mb-3" />
            <h1 className="font-display text-3xl font-bold text-foreground">Application under review</h1>
            <p className="mt-2 text-muted-foreground">Thanks for applying! Your requested code <span className="font-semibold text-primary">{data.application?.requested_code}</span> is reserved while we review. We’ll unlock your collaborator access soon.</p>
          </div>
        )}

        {/* APPLY FORM (no application yet, or rejected) */}
        {(status === null || status === 'rejected') && (
          <div data-testid="collab-apply">
            <div className="flex items-center gap-2 mb-1">
              <Handshake className="h-6 w-6 text-primary" />
              <Badge className="bg-secondary/15 text-secondary border-secondary/30">Collaborator Program</Badge>
            </div>
            <h1 className="font-display text-4xl font-bold text-foreground">Partner with Lumera</h1>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Pastors, teachers & creators: get <strong className="text-foreground">Lumera Premium + Slidecon free</strong> while you’re an active collaborator, your own
              <strong className="text-foreground"> custom referral code</strong>, and a dashboard tracking every sign-up. Your audience gets a
              <strong className="text-foreground"> 15-day free trial</strong> of Lumera + Slidecon.
            </p>
            {status === 'rejected' && (
              <p className="mt-3 rounded-lg bg-destructive/10 px-4 py-2 text-sm text-destructive">A previous application wasn’t approved{data.application?.reject_reason ? `: ${data.application.reject_reason}` : ''}. You’re welcome to re-apply.</p>
            )}

            <h2 className="mt-8 mb-3 font-display text-2xl font-bold text-foreground">What you get</h2>
            <PerksGrid />

            <form onSubmit={submitApplication} className="mt-8 space-y-4 rounded-xl border border-border bg-card p-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Desired code <span className="text-muted-foreground font-normal">(A-Z, 0-9)</span></Label>
                  <div className="relative">
                    <Gift className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="code" data-testid="collab-code-input" required maxLength={20}
                      placeholder="JANE" value={form.requested_code}
                      onChange={(e) => setForm({ ...form, requested_code: e.target.value.toUpperCase() })}
                      className="pl-10 uppercase" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="platform">Platform</Label>
                  <Input id="platform" data-testid="collab-platform-input" placeholder="YouTube, Instagram, Church…"
                    value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="handle">Handle / channel</Label>
                  <Input id="handle" placeholder="@yourname"
                    value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="audience">Audience size</Label>
                  <Input id="audience" placeholder="e.g. 25k subscribers"
                    value={form.audience_size} onChange={(e) => setForm({ ...form, audience_size: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Tell us about your ministry / audience</Label>
                <Textarea id="message" rows={3} placeholder="How will you share Lumera?"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              <Button type="submit" disabled={submitting} className="w-full sm:w-auto" data-testid="collab-submit-btn">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : <><Sparkles className="h-4 w-4 mr-2" /> Apply to collaborate</>}
              </Button>
            </form>
          </div>
        )}

        {/* ADMIN — review pending applications */}
        {isAdmin && apps && apps.length > 0 && (
          <div className="mt-12" data-testid="collab-admin-panel">
            <h2 className="font-display text-2xl font-bold text-foreground mb-4">Applications to review ({apps.length})</h2>
            <div className="space-y-3">
              {apps.map((a) => (
                <div key={a.id} className="rounded-xl border border-border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`collab-app-${a.requested_code}`}>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">{a.name} · <span className="text-primary">{a.requested_code}</span></div>
                    <div className="text-sm text-muted-foreground">{a.email} — {a.platform} {a.handle} · {a.audience_size}</div>
                    {a.message && <div className="text-sm text-muted-foreground mt-1 italic">“{a.message}”</div>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => review(a.id, 'approve')} data-testid={`collab-approve-${a.requested_code}`}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => review(a.id, 'reject')} data-testid={`collab-reject-${a.requested_code}`}>Reject</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ChartBars = ({ series }) => {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <div className="flex items-end gap-[3px] h-28" data-testid="collab-chart">
      {series.map((s) => (
        <div key={s.date} className="flex-1 group relative flex flex-col justify-end">
          <div
            className="w-full rounded-t bg-primary/70 hover:bg-primary transition-colors"
            style={{ height: `${(s.count / max) * 100}%`, minHeight: s.count > 0 ? '4px' : '2px', opacity: s.count > 0 ? 1 : 0.25 }}
            title={`${s.date}: ${s.count}`}
          />
        </div>
      ))}
    </div>
  );
};

export default CollabPage;
